import React, { useState, useCallback } from "react";
import {
  Box,
  Collapse,
  IconButton,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DataTable from "bundles/Internal/components/common/DataTable";
import TimeProjectTableFilters, { EMPTY_FILTERS } from "./TimeProjectTableFilters";

function formatDuration(secs) {
  if (!secs || secs === 0) return "0m";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const COLUMNS = [
  { key: "name", label: "Project" },
  { key: "company", label: "Company" },
  { key: "pipeline", label: "Pipeline" },
  { key: "users", label: "Users" },
  { key: "total_duration", label: "Total" },
  { key: "entries", label: "Entries" },
  { key: "last_activity", label: "Last activity" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
];

function renderUserAvatars(users, size = 22) {
  if (!users || users.length === 0) return "";
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {users.slice(0, 4).map((u) => (
        <Tooltip key={u.id} title={u.full_name || "Unknown"} arrow placement="top">
          <UserAvatar
            user={u}
            size={size}
            sx={{ ml: "-4px", "&:first-of-type": { ml: 0 } }}
            tooltip={false}
          />
        </Tooltip>
      ))}
      {users.length > 4 && (
        <Box component="span" sx={{ fontSize: 11, color: "rgba(42, 51, 78, 0.5)", ml: 0.5 }}>
          +{users.length - 4}
        </Box>
      )}
    </Box>
  );
}

function formatProjectCell(row, key) {
  switch (key) {
  case "name":
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: row.color || "#ccc",
            flexShrink: 0,
          }}
        />
        <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
          {row.name || "—"}
        </Box>
      </Box>
    );

  case "company":
    if (!row.company_id || !row.company_name) return "";
    return (
      <Typography
        variant="body2"
        component={RouterLink}
        to={`/internal/companies/${row.company_id}`}
        onClick={(e) => e.stopPropagation()}
        sx={{ color: "#079344", fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
      >
        {row.company_name}
      </Typography>
    );

  case "pipeline":
    if (!row.profile_pipeline_id || !row.pipeline_name) return "";
    return (
      <Typography
        variant="body2"
        component={RouterLink}
        to={`/internal/pipelines/${row.profile_pipeline_id}`}
        onClick={(e) => e.stopPropagation()}
        sx={{ color: "#079344", fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
      >
        {row.pipeline_name}
      </Typography>
    );

  case "users":
    return renderUserAvatars(row.users);

  case "total_duration":
    return (
      <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
        {formatDuration(row.total_duration_secs)}
      </Box>
    );

  case "entries":
    return row.total_entries ?? 0;

  case "last_activity":
    return formatRelativeTime(row.last_activity_at);

  case "week":
    return formatDuration(row.week_duration);

  case "month":
    return formatDuration(row.month_duration);

  default:
    return null;
  }
}

const subCellSx = {
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "#2A334E",
  py: 0.75,
  borderBottom: "1px solid rgba(42,51,78,0.06)",
};

const TimeProjectTable = ({
  rows,
  onQueryChange,
  filters,
  onFiltersChange,
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading,
  onRowClick,
}) => {
  const [expandedRows, setExpandedRows] = useState({});

  const renderCell = useCallback((row, key) => formatProjectCell(row, key), []);

  const toggleExpand = useCallback((e, projectId) => {
    e.stopPropagation();
    setExpandedRows((prev) => ({ ...prev, [projectId]: !prev[projectId] }));
  }, []);

  const renderExpandedRow = useCallback(
    (row) => {
      if (!expandedRows[row.id]) return null;
      const tasks = row.task_breakdown || [];
      if (tasks.length === 0) {
        return (
          <Box sx={{ px: 4, py: 2, fontSize: 13, color: "rgba(42,51,78,0.5)" }}>
            No task data available.
          </Box>
        );
      }
      const totalProjectSecs = tasks.reduce((sum, t) => sum + (t.total_secs || 0), 0);
      return (
        <Collapse in timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 3,
              py: 2,
              backgroundColor: "rgba(249,250,255,0.7)",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 1.5,
            }}
          >
            {tasks.map((task) => {
              const pct = totalProjectSecs > 0 ? Math.round((task.total_secs / totalProjectSecs) * 100) : 0;
              return (
                <Box
                  key={task.task_name}
                  sx={{
                    p: 2,
                    borderRadius: "0.75rem",
                    backgroundColor: "#fff",
                    border: "1px solid hsl(140 8% 90%)",
                    "&:hover": {
                      borderColor: row.color || "hsl(140 8% 82%)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    },
                    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: row.color || "#ccc",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: 13,
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        color: "#2A334E",
                      }}
                    >
                      {task.task_name}
                    </Typography>
                    <Box sx={{ flexShrink: 0 }}>
                      {renderUserAvatars(task.users, 20)}
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mb: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        color: "#2A334E",
                        lineHeight: 1,
                      }}
                    >
                      {formatDuration(task.total_secs)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "rgba(42,51,78,0.5)" }}>
                      · {task.entry_count} {task.entry_count === 1 ? "entry" : "entries"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: "rgba(0,0,0,0.04)", overflow: "hidden" }}>
                      <Box
                        sx={{
                          width: `${pct}%`,
                          height: "100%",
                          borderRadius: 2,
                          bgcolor: row.color || "#ccc",
                          opacity: 0.7,
                          transition: "width 0.3s ease",
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: 10, color: "rgba(42,51,78,0.45)", flexShrink: 0 }}>
                      {pct}%
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: 11, color: "rgba(42,51,78,0.55)" }}>
                      {formatDuration(task.avg_entry_secs)} avg
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "rgba(42,51,78,0.4)" }}>
                      {formatRelativeTime(task.last_activity_at)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      );
    },
    [expandedRows],
  );

  return (
    <DataTable
      title="Project Time Stats"
      totalLabel={(count) => `${count.toLocaleString()} total projects`}
      emptyIcon={AccessTimeIcon}
      emptyTitle="No projects found"
      emptySubtitle="Try adjusting your search criteria"
      columns={COLUMNS}
      data={rows}
      renderCell={renderCell}
      onRowClick={onRowClick}
      extraHeadColumns={[""]}
      renderExtraCells={(row) => (
        <TableCell key="expand" sx={{ whiteSpace: "nowrap", py: 1.5, width: 36, px: 0.5 }}>
          {(row.task_breakdown || []).length > 0 && (
            <IconButton
              size="small"
              aria-label={expandedRows[row.id] ? "Collapse" : "Expand"}
              onClick={(e) => toggleExpand(e, row.id)}
              disableRipple
              sx={{
                color: expandedRows[row.id] ? "rgba(42, 51, 78, 0.7)" : "rgba(42, 51, 78, 0.3)",
                "&:hover": { color: "rgba(42, 51, 78, 0.7)" },
                transition: "transform 0.2s",
              }}
            >
              {expandedRows[row.id] ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
      )}
      renderExpandedRow={renderExpandedRow}
      onQueryChange={onQueryChange}
      searchPlaceholder="Search by project name..."
      searchInfo="Project name"
      searchById
      filtersComponent={
        filters && onFiltersChange ? (
          <TimeProjectTableFilters filters={filters} onFiltersChange={onFiltersChange} />
        ) : null
      }
      page={page}
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      loading={loading}
    />
  );
};

export default TimeProjectTable;
